import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Divider,
  FormHelperText
} from '@mui/material';
import { Edit, Save, Language, Security, Timer } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { Customer } from '../../types/exporter';
import { Website } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface EditWebsiteFormData {
  name: string;
  customerId: number | null;
  checkInterval: string;
  enabled: boolean;
}

interface EditWebsiteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (id: number, data: EditWebsiteFormData) => Promise<boolean>;
  website: Website | null;
  customers?: Customer[];
}

// ============================================================================
// Constants
// ============================================================================

const CHECK_INTERVALS = [
  { value: '30s', label: '30 seconds' },
  { value: '60s', label: '1 minute' },
  { value: '2m', label: '2 minutes' },
  { value: '5m', label: '5 minutes' }
];

// ============================================================================
// Component
// ============================================================================

export const EditWebsiteDialog: React.FC<EditWebsiteDialogProps> = ({
  open,
  onClose,
  onSave,
  website,
  customers = []
}) => {
  const theme = useTheme();

  // Form state
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [checkInterval, setCheckInterval] = useState('60s');
  const [enabled, setEnabled] = useState(true);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Populate form when website changes
  useEffect(() => {
    if (website) {
      setName(website.name || '');
      setCustomerId(website.customerId ?? null);
      setCheckInterval(website.checkInterval || '60s');
      setEnabled(website.enabled ?? true);
      setHasChanges(false);
    }
  }, [website]);

  // Track changes
  useEffect(() => {
    if (website) {
      const changed =
        name !== (website.name || '') ||
        customerId !== (website.customerId ?? null) ||
        checkInterval !== (website.checkInterval || '60s') ||
        enabled !== (website.enabled ?? true);
      setHasChanges(changed);
    }
  }, [name, customerId, checkInterval, enabled, website]);

  // Reset form when dialog closes
  const handleClose = () => {
    setName('');
    setCustomerId(null);
    setCheckInterval('60s');
    setEnabled(true);
    setError('');
    setHasChanges(false);
    onClose();
  };

  // Handle form submission
  const handleSave = async () => {
    if (!website) return;

    setSubmitting(true);
    setError('');

    try {
      const formData: EditWebsiteFormData = {
        name: name || website.name,
        customerId,
        checkInterval,
        enabled
      };

      const success = await onSave(website.id, formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error('Save website failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!website) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '400px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Edit sx={{ mr: 1 }} />
            Edit Website
          </Box>
          <Chip
            label={website.status.toUpperCase()}
            color={getStatusColor(website.status)}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Read-only URL Info */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Website Details (Read Only)
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.grey[50],
              borderRadius: 1,
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Language sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1" fontWeight="medium">
                {website.url}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 4 }}>
              {website.responseTime !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Response Time
                  </Typography>
                  <Typography variant="body2">
                    {website.responseTime}ms
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uptime
                </Typography>
                <Typography variant="body2">
                  {website.uptime.toFixed(2)}%
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                The URL cannot be changed after creation. To monitor a different URL,
                create a new website entry and delete this one.
              </Typography>
            </Alert>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Editable Details */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Editable Settings
          </Typography>

          <TextField
            fullWidth
            label="Display Name"
            placeholder="e.g., Company Website"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Friendly name shown in the dashboard"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Check Interval</InputLabel>
              <Select
                value={checkInterval}
                onChange={(e: SelectChangeEvent) => setCheckInterval(e.target.value)}
                label="Check Interval"
              >
                {CHECK_INTERVALS.map((interval) => (
                  <MenuItem key={interval.value} value={interval.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Timer sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      {interval.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>How often to check the website</FormHelperText>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value === '' ? null : Number(e.target.value))}
                label="Customer"
              >
                <MenuItem value="">
                  <em>Internal / No Customer</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.cust_id} value={customer.cust_id}>
                    {customer.cust_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Assign to a customer</FormHelperText>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Enable/Disable Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: enabled ? theme.palette.success.light + '20' : theme.palette.grey[100],
              borderRadius: 1,
              border: `1px solid ${enabled ? theme.palette.success.light : theme.palette.grey[300]}`
            }}
          >
            <Box>
              <Typography variant="subtitle2">
                Monitoring Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {enabled
                  ? 'This website is actively being monitored'
                  : 'Monitoring is disabled for this website'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="success"
                />
              }
              label={enabled ? 'Enabled' : 'Disabled'}
            />
          </Box>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges}
          loading={submitting}
          startIcon={<Save />}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditWebsiteDialog;
